import React, { useState } from 'react';
import { auth } from '../firebase';
import { loadLatestMetrics } from '../db';

const PROGRAMMES = [
  {
    id: 'stress-workshop',
    name: 'Stress Management Workshop',
    duration: '1 day',
    type: 'Workshop',
    desc: 'Evidence-based MBSR and cognitive reframing techniques. Addresses cortisol regulation, anxiety tools and sleep protocol.',
    ideal: 'Teams with elevated stress scores (above 60/100)',
    color: '#f59e0b'
  },
  {
    id: 'executive-coaching',
    name: 'Executive Coaching',
    duration: 'Ongoing',
    type: 'Coaching',
    desc: 'One-to-one executive coaching for senior leaders. Addresses burnout recovery, decision-making under pressure and leadership resilience.',
    ideal: 'Senior leaders and C-suite with burnout risk',
    color: '#ef4444'
  },
  {
    id: '3-month-course',
    name: '3-Month Comprehensive Course',
    duration: '12 weeks',
    type: 'Course',
    desc: 'Weekly workshops combined with a personalised 12-week health coaching plan. Covers stress, nutrition, fitness and sleep.',
    ideal: 'Teams needing sustained transformation',
    color: '#00AEEF'
  },
  {
    id: 'leadership-resilience',
    name: 'Leadership Resilience Workshop',
    duration: '1-2 days',
    type: 'Workshop',
    desc: 'Intensive programme addressing chronic stress physiology, executive burnout recovery and sustainable high-performance frameworks.',
    ideal: 'Leadership teams with critical burnout indicators',
    color: '#ef4444'
  },
  {
    id: 'performance-leadership',
    name: 'Performance Leadership Programme',
    duration: '6-12 months',
    type: 'Programme',
    desc: 'Elite programme for senior leaders combining advanced health analytics, monthly coaching and performance optimisation.',
    ideal: 'High-performing executives seeking peak performance',
    color: '#8b5cf6'
  },
  {
    id: '1-year-programme',
    name: '1-Year Programme',
    duration: '12 months',
    type: 'Premium',
    desc: 'Full year access to all workshops, comprehensive bloodwork analysis, monthly coaching check-ins and long-term performance optimisation.',
    ideal: 'Organisations committed to sustained culture change',
    color: '#10b981'
  }
];

const TEAM_SIZES = [
  { value: '1-10', label: '1–10 employees' },
  { value: '11-25', label: '11–25 employees' },
  { value: '26-50', label: '26–50 employees' },
  { value: '51-100', label: '51–100 employees' },
  { value: '100+', label: '100+ employees' }
];

const URGENCY = [
  { value: 'asap', label: 'As soon as possible' },
  { value: '1month', label: 'Within 1 month' },
  { value: '3months', label: 'Within 3 months' },
  { value: 'flexible', label: 'Flexible / planning ahead' }
];

export default function InterventionBooking({ companyData }) {
  const [selectedProgramme, setSelectedProgramme] = useState(null);
  const [stage, setStage] = useState('select'); // select | form | sent
  const [submitting, setSubmitting] = useState(false);
  const [form, setForm] = useState({
    contactName: '',
    contactEmail: auth.currentUser?.email || '',
    companyName: '',
    teamSize: '',
    urgency: '',
    additionalContext: '',
    companyCode: companyData?.code || ''
  });

  const avgStress = companyData?.mental?.length > 0
    ? (companyData.mental.reduce((s, m) => s + (parseFloat(m.stressScore) || 0), 0) / companyData.mental.length).toFixed(0)
    : null;
  const avgBurnout = companyData?.mental?.length > 0
    ? (companyData.mental.reduce((s, m) => s + (parseFloat(m.burnoutScore) || 0), 0) / companyData.mental.length).toFixed(0)
    : null;

  async function handleSubmit() {
    if (!form.contactName || !form.contactEmail || !selectedProgramme) return;
    setSubmitting(true);

    const subject = `Levare Platform — Intervention Request: ${selectedProgramme.name}`;
    const body = `
New intervention request from the Levare Health Intelligence Platform

Programme Requested: ${selectedProgramme.name}
Duration: ${selectedProgramme.duration}
Type: ${selectedProgramme.type}

Contact Details:
- Name: ${form.contactName}
- Email: ${form.contactEmail}
- Company: ${form.companyName || 'Not provided'}
- Company Code: ${form.companyCode || 'Not linked'}
- Team Size: ${form.teamSize || 'Not specified'}
- Urgency: ${form.urgency || 'Not specified'}

Team Health Data Summary:
- Employees on platform: ${companyData?.totalEmployees || 'Unknown'}
- Average Stress Score: ${avgStress ? avgStress + '/100' : 'No data'}
- Average Burnout Score: ${avgBurnout ? avgBurnout + '/100' : 'No data'}
- Employees with mental health data: ${companyData?.mental?.length || 0}

Additional Context:
${form.additionalContext || 'None provided'}

---
Sent from Levare Health Intelligence Platform
    `.trim();

    const mailtoLink = `mailto:contact@levarewellbeing.com?subject=${encodeURIComponent(subject)}&body=${encodeURIComponent(body)}`;
    window.open(mailtoLink, '_blank');

    setSubmitting(false);
    setStage('sent');
  }

  if (stage === 'sent') return (
    <div style={{ textAlign: 'center', padding: '2rem 1rem' }}>
      <div style={{ fontSize: '2.5rem', marginBottom: '1rem' }}>✓</div>
      <div style={{ fontSize: '1rem', fontWeight: 500, color: 'var(--color-text-primary)', marginBottom: '.5rem' }}>Request sent to Levare</div>
      <div style={{ fontSize: '.82rem', color: 'var(--color-text-secondary)', marginBottom: '1.5rem', lineHeight: 1.6 }}>
        Your email client should have opened with a pre-filled message to contact@levarewellbeing.com. The Levare team will be in touch within 24 hours to discuss your requirements.
      </div>
      <div style={{ display: 'flex', gap: '.75rem', justifyContent: 'center', marginBottom: '1.5rem' }}>
        <a href="https://cal.com/levare-wellbeing/30min" target="_blank" rel="noopener noreferrer"
          style={{ padding: '.65rem 1.25rem', borderRadius: '8px', background: '#00AEEF', color: '#fff', textDecoration: 'none', fontSize: '.85rem', fontWeight: 500 }}>
          Book a 30-min call directly
        </a>
        <button onClick={() => { setStage('select'); setSelectedProgramme(null); }}
          style={{ padding: '.65rem 1.25rem', borderRadius: '8px', border: '0.5px solid var(--color-border-secondary)', background: 'transparent', color: 'var(--color-text-secondary)', fontSize: '.85rem', cursor: 'pointer' }}>
          Request another programme
        </button>
      </div>
    </div>
  );

  if (stage === 'form') return (
    <div>
      <div style={{ display: 'flex', alignItems: 'center', gap: '.75rem', marginBottom: '1.25rem' }}>
        <button onClick={() => setStage('select')} style={{ background: 'none', border: 'none', color: 'var(--color-text-secondary)', cursor: 'pointer', fontSize: '.8rem' }}>← Back</button>
        <div>
          <div style={{ fontSize: '.88rem', fontWeight: 500 }}>Request: {selectedProgramme.name}</div>
          <div style={{ fontSize: '.72rem', color: 'var(--color-text-secondary)' }}>{selectedProgramme.duration} · {selectedProgramme.type}</div>
        </div>
      </div>

      <div style={{ display: 'grid', gridTemplateColumns: '1fr 1fr', gap: '1rem' }}>
        <div style={{ background: 'var(--color-background-primary)', border: '0.5px solid var(--color-border-tertiary)', borderRadius: '12px', padding: '1.25rem' }}>
          <div style={{ fontSize: '.85rem', fontWeight: 500, marginBottom: '1rem' }}>Your Details</div>
          {[
            ['Your Name', 'contactName', 'text', 'e.g. Sarah Johnson'],
            ['Your Email', 'contactEmail', 'email', 'your@company.com'],
            ['Company Name', 'companyName', 'text', 'e.g. Acme Corp'],
          ].map(([label, key, type, placeholder]) => (
            <div key={key} style={{ marginBottom: '.75rem' }}>
              <label style={{ fontSize: '.72rem', color: 'var(--color-text-secondary)', display: 'block', marginBottom: '.25rem', textTransform: 'uppercase', letterSpacing: '.05em' }}>{label}</label>
              <input type={type} value={form[key]} onChange={e => setForm(p => ({ ...p, [key]: e.target.value }))} placeholder={placeholder} style={{ width: '100%' }} />
            </div>
          ))}
          <div style={{ marginBottom: '.75rem' }}>
            <label style={{ fontSize: '.72rem', color: 'var(--color-text-secondary)', display: 'block', marginBottom: '.25rem', textTransform: 'uppercase', letterSpacing: '.05em' }}>Team Size</label>
            <select value={form.teamSize} onChange={e => setForm(p => ({ ...p, teamSize: e.target.value }))} style={{ width: '100%' }}>
              <option value="">Select...</option>
              {TEAM_SIZES.map(o => <option key={o.value} value={o.value}>{o.label}</option>)}
            </select>
          </div>
          <div style={{ marginBottom: '.75rem' }}>
            <label style={{ fontSize: '.72rem', color: 'var(--color-text-secondary)', display: 'block', marginBottom: '.25rem', textTransform: 'uppercase', letterSpacing: '.05em' }}>When do you need this?</label>
            <select value={form.urgency} onChange={e => setForm(p => ({ ...p, urgency: e.target.value }))} style={{ width: '100%' }}>
              <option value="">Select...</option>
              {URGENCY.map(o => <option key={o.value} value={o.value}>{o.label}</option>)}
            </select>
          </div>
        </div>

        <div>
          <div style={{ background: 'var(--color-background-primary)', border: '0.5px solid var(--color-border-tertiary)', borderRadius: '12px', padding: '1.25rem', marginBottom: '1rem' }}>
            <div style={{ fontSize: '.85rem', fontWeight: 500, marginBottom: '.75rem' }}>Additional Context</div>
            <textarea value={form.additionalContext} onChange={e => setForm(p => ({ ...p, additionalContext: e.target.value }))}
              placeholder="Tell us more about your team's situation, any specific challenges, or what outcomes you're hoping to achieve..."
              rows={5} style={{ width: '100%', resize: 'vertical', fontFamily: 'var(--font-sans)', fontSize: '.82rem' }} />
          </div>

          {(avgStress || avgBurnout) && (
            <div style={{ background: 'var(--color-background-primary)', border: '0.5px solid var(--color-border-tertiary)', borderRadius: '12px', padding: '1.25rem', marginBottom: '1rem' }}>
              <div style={{ fontSize: '.82rem', fontWeight: 500, marginBottom: '.6rem' }}>Team data included in request</div>
              <div style={{ fontSize: '.72rem', color: 'var(--color-text-secondary)', lineHeight: 1.5 }}>
                Your team's anonymised health data will be shared with Levare to help them recommend the right approach.
              </div>
              <div style={{ marginTop: '.6rem', display: 'flex', gap: '.4rem', flexWrap: 'wrap' }}>
                {avgStress && <span style={{ fontSize: '.68rem', padding: '.2rem .5rem', borderRadius: '4px', background: 'var(--color-background-secondary)', color: 'var(--color-text-secondary)' }}>Avg Stress: {avgStress}/100</span>}
                {avgBurnout && <span style={{ fontSize: '.68rem', padding: '.2rem .5rem', borderRadius: '4px', background: 'var(--color-background-secondary)', color: 'var(--color-text-secondary)' }}>Avg Burnout: {avgBurnout}/100</span>}
                <span style={{ fontSize: '.68rem', padding: '.2rem .5rem', borderRadius: '4px', background: 'var(--color-background-secondary)', color: 'var(--color-text-secondary)' }}>{companyData?.totalEmployees || 0} employees</span>
              </div>
            </div>
          )}

          <button onClick={handleSubmit} disabled={submitting || !form.contactName || !form.contactEmail}
            style={{ width: '100%', padding: '.75rem', borderRadius: '8px', border: 'none', background: '#00AEEF', color: '#fff', fontFamily: 'var(--font-sans)', fontSize: '.88rem', cursor: 'pointer', fontWeight: 500, opacity: (submitting || !form.contactName || !form.contactEmail) ? 0.6 : 1, marginBottom: '.5rem' }}>
            {submitting ? 'Sending...' : 'Send Request to Levare'}
          </button>
          <a href="https://cal.com/levare-wellbeing/30min" target="_blank" rel="noopener noreferrer"
            style={{ display: 'block', textAlign: 'center', padding: '.65rem', borderRadius: '8px', border: '0.5px solid var(--color-border-secondary)', color: 'var(--color-text-secondary)', textDecoration: 'none', fontSize: '.82rem' }}>
            Or book a 30-min call directly →
          </a>
        </div>
      </div>
    </div>
  );

  return (
    <div>
      <div style={{ fontSize: '1.3rem', fontWeight: 700, marginBottom: '.25rem' }}>Request an Intervention</div>
      <div style={{ fontSize: '.78rem', color: 'var(--color-text-secondary)', marginBottom: '1.25rem' }}>Select a Levare programme to request for your team</div>

      {(avgStress && parseFloat(avgStress) > 60) && (
        <div style={{ padding: '.7rem 1rem', borderRadius: '8px', fontSize: '.78rem', marginBottom: '1rem', background: 'rgba(245,158,11,.08)', border: '1px solid rgba(245,158,11,.2)', color: '#fbbf24' }}>
          Your team's average stress score is {avgStress}/100. Levare recommends the Stress Management Workshop or Leadership Resilience Workshop as a priority.
        </div>
      )}

      <div style={{ display: 'grid', gridTemplateColumns: '1fr 1fr', gap: '1rem' }}>
        {PROGRAMMES.map(prog => (
          <div key={prog.id}
            onClick={() => { setSelectedProgramme(prog); setStage('form'); }}
            style={{ background: 'var(--color-background-primary)', border: `0.5px solid var(--color-border-tertiary)`, borderRadius: '12px', padding: '1.25rem', cursor: 'pointer', transition: 'all .2s', position: 'relative', overflow: 'hidden' }}
            onMouseEnter={e => { e.currentTarget.style.borderColor = prog.color; e.currentTarget.style.transform = 'translateY(-2px)'; }}
            onMouseLeave={e => { e.currentTarget.style.borderColor = 'var(--color-border-tertiary)'; e.currentTarget.style.transform = 'none'; }}>
            <div style={{ position: 'absolute', top: 0, left: 0, width: '100%', height: '3px', background: prog.color }} />
            <div style={{ display: 'flex', justifyContent: 'space-between', alignItems: 'flex-start', marginBottom: '.5rem' }}>
              <div style={{ fontSize: '.88rem', fontWeight: 500, color: 'var(--color-text-primary)', paddingRight: '.5rem' }}>{prog.name}</div>
              <span style={{ fontSize: '.65rem', padding: '.2rem .5rem', borderRadius: '4px', background: 'var(--color-background-secondary)', color: 'var(--color-text-secondary)', flexShrink: 0 }}>{prog.duration}</span>
            </div>
            <div style={{ fontSize: '.72rem', color: 'var(--color-text-secondary)', marginBottom: '.6rem', lineHeight: 1.5 }}>{prog.desc}</div>
            <div style={{ fontSize: '.7rem', color: prog.color, fontStyle: 'italic' }}>Best for: {prog.ideal}</div>
            <div style={{ marginTop: '.85rem', fontSize: '.78rem', color: '#00AEEF', fontWeight: 500 }}>Request this programme →</div>
          </div>
        ))}
      </div>

      <div style={{ marginTop: '1.25rem', padding: '1rem 1.25rem', background: 'var(--color-background-primary)', border: '0.5px solid var(--color-border-tertiary)', borderRadius: '12px', display: 'flex', alignItems: 'center', justifyContent: 'space-between', flexWrap: 'wrap', gap: '.75rem' }}>
        <div>
          <div style={{ fontSize: '.82rem', fontWeight: 500, marginBottom: '.2rem' }}>Not sure which programme is right?</div>
          <div style={{ fontSize: '.75rem', color: 'var(--color-text-secondary)' }}>Book a free 30-minute discovery call with the Levare team.</div>
        </div>
        <a href="https://cal.com/levare-wellbeing/30min" target="_blank" rel="noopener noreferrer"
          style={{ padding: '.6rem 1.25rem', borderRadius: '8px', background: '#00AEEF', color: '#fff', textDecoration: 'none', fontSize: '.82rem', fontWeight: 500, flexShrink: 0 }}>
          Book a discovery call
        </a>
      </div>
    </div>
  );
}
